import type { CSSProperties } from 'react'
import type { Icon3Props } from 'opensolar-ui/src/core/Icon'
import type { ProjectServiceTypeData } from 'types/projects'

export type ProjectServiceCategory =
  | 'solar'
  | 'battery'
  | 'electrical'
  | 'ev'
  | 'heat_pump'
  | 'hvac'
  | 'roofing'
  | 'other'

export type ProjectServiceCategorySection = 'solar' | 'services'

export type ProjectServiceCategoryDefinition = {
  id: ProjectServiceCategory
  name: string
  icon: Icon3Props['icon']
  /** Accent color from Non-Solar Projects Figma (icon + selected chip). */
  color: string
  section: ProjectServiceCategorySection
}

/** Figma token colors: OS Yellow/800, Violet/800, Orange/800, Green/800, Blue/800, Teal/800, Magenta/800, Neutral Grey/900 */
export const PROJECT_SERVICE_CATEGORY_DEFINITIONS: ProjectServiceCategoryDefinition[] = [
  { id: 'solar', name: 'Solar', icon: 'wb_sunny', color: '#FFCC26', section: 'solar' },
  { id: 'battery', name: 'Battery', icon: 'battery_1_bar', color: '#6E23C9', section: 'services' },
  { id: 'electrical', name: 'Electrical', icon: 'electric_bolt', color: '#EB6424', section: 'services' },
  { id: 'ev', name: 'EV', icon: 'electric_car', color: '#019939', section: 'services' },
  { id: 'heat_pump', name: 'Heat Pump', icon: 'heat_pump', color: '#4272DD', section: 'services' },
  { id: 'hvac', name: 'HVAC', icon: 'hvac', color: '#007092', section: 'services' },
  { id: 'roofing', name: 'Roofing', icon: 'roofing', color: '#B01F80', section: 'services' },
  { id: 'other', name: 'Other', icon: 'build', color: '#6B6A76', section: 'services' },
]

export const PROJECT_SERVICE_CATEGORY_BY_ID = Object.fromEntries(
  PROJECT_SERVICE_CATEGORY_DEFINITIONS.map((definition) => [definition.id, definition])
) as Record<ProjectServiceCategory, ProjectServiceCategoryDefinition>

export const PROJECT_SERVICE_CATEGORY_SECTIONS: {
  section: ProjectServiceCategorySection
  label: string
}[] = [
  { section: 'solar', label: 'Solar' },
  { section: 'services', label: 'Services' },
]

export const getProjectServiceCategoryColor = (serviceType: ProjectServiceCategory | string): string =>
  PROJECT_SERVICE_CATEGORY_BY_ID[serviceType as ProjectServiceCategory]?.color ?? '#6B6A76'

export const getProjectServiceChipBodyStyle = (serviceType: ProjectServiceCategory | string): CSSProperties => {
  const color = getProjectServiceCategoryColor(serviceType)
  return {
    backgroundColor: `${color}1A`,
    borderColor: `${color}66`,
    color,
  }
}

export const getProjectServiceChipIconStyle = (serviceType: ProjectServiceCategory | string): CSSProperties => ({
  color: getProjectServiceCategoryColor(serviceType),
})

export const getProjectServiceChipLabelStyle = (serviceType: ProjectServiceCategory | string): CSSProperties => ({
  color: getProjectServiceCategoryColor(serviceType),
  fontWeight: 500,
})

export const parseProjectServiceTypesValue = (
  value: string | ProjectServiceCategory[] | undefined | null
): ProjectServiceCategory[] => {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean) as ProjectServiceCategory[]
  return value.split(',').filter(Boolean) as ProjectServiceCategory[]
}

/** List/detail payloads expose read-only `service_types_data`; the writable `service_types` field is form-only. */
export const resolveProjectServiceTypes = ({
  service_types,
  service_types_data,
}: {
  service_types?: string | ProjectServiceCategory[] | null
  service_types_data?: ProjectServiceTypeData[] | null
}): ProjectServiceCategory[] => {
  const fromWritableField = parseProjectServiceTypesValue(service_types)
  if (fromWritableField.length) return fromWritableField
  if (!Array.isArray(service_types_data)) return []
  return service_types_data
    .map((row) => row.service_type)
    .filter(Boolean) as ProjectServiceCategory[]
}

export const serializeProjectServiceTypesValue = (values: ProjectServiceCategory[]): string => values.join(',')

/** useInput parse: accept array from onChange or string already stored on the form. */
export const parseProjectServiceTypesField = (
  value: string | ProjectServiceCategory[] | undefined | null
): string => {
  if (Array.isArray(value)) return serializeProjectServiceTypesValue(value)
  return value ?? ''
}
